import React, { useState, useEffect } from 'react';
import { useLocation } from '@reach/router';
import DOMPurify from 'dompurify';
import { ArrayHelpers, FieldArray, FormikValues, getIn, useFormikContext } from 'formik';
import styled from 'styled-components';
import { CheckboxDesign, QuestionOption } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import PlusIcon from '@images/icon-plus-xs.svg';
import { replaceText } from '@utils/handleTextReplacement';
import Colors from '../Colors';
import { InputDescription, InputLabel } from '../Typography';
import Checkbox from './Checkbox';
import FormFieldError from './FormFieldError';

interface Props {
  values: FormikValues;
  label?: string;
  description?: string;
  options: QuestionOption[];
  design?: string;
  id: string;
  errorMessage?: string;
}

const MultiCheckbox: React.FC<Props> = ({ values, label, description, options, id, design = 'standard' }) => {
  const { country } = useCountry();
  const location = useLocation();
  const { setFieldValue, errors, touched } = useFormikContext();
  const sortedOptions = [...(options ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const specialOptions = ['none', 'unsure', 'prefer-not-to-say'];
  const currentValues = Array.isArray(values[id]) ? values[id] : [];
  const initialTicked = currentValues.some((value) => specialOptions.includes(value)) ?? false;
  const [isSpecialTicked, setIsSpecialTicked] = useState<boolean>(initialTicked);
  const fieldError = getIn(errors, id);
  const fieldTouched = getIn(touched, id);

  useEffect(() => {
    if (!values[id]) {
      setFieldValue(id, []);
    }

    const specialTicked = Array.isArray(values[id]) && values[id].some((value) => specialOptions.includes(value));
    setIsSpecialTicked(specialTicked);
  }, [values, id, setFieldValue]);

  const handleChange = (option: QuestionOption, isChecked: boolean, arrayHelpers: ArrayHelpers) => {
    const currentFieldValues = Array.isArray(values[id]) ? values[id] : [];

    if (isChecked) {
      if (specialOptions.includes(option.value)) {
        setIsSpecialTicked(true);
        setFieldValue(id, [option.value]);
      } else if (!currentFieldValues.includes(option.value)) {
        arrayHelpers.push(option.value);
      }
    } else if (specialOptions.includes(option.value)) {
      setIsSpecialTicked(false);
      setFieldValue(id, []);
    } else {
      const index = currentFieldValues.findIndex((item) => item === option.value);
      if (index !== -1) {
        arrayHelpers.remove(index);
      }
    }
  };

  return (
    <Container>
      <FieldArray
        name={id}
        render={(arrayHelpers) => (
          <InnerWrapper>
            <QuestionHeading>
              {label && <InputLabel>{replaceText(label, country, location)}</InputLabel>}
              {description && (
                <InputDescription dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(replaceText(description, country, location)) }} />
              )}
            </QuestionHeading>
            <CheckboxesWrap $design={design}>
              {sortedOptions.map((option) => {
                const fieldValues = values[id] || [];
                const isChecked = fieldValues.includes(option.value);
                const hasSelectedValues = fieldValues.length > 0;
                let isDisabled = false;

                if (option.value === undefined) {
                  isDisabled = false;
                } else if (specialOptions.includes(option.value)) {
                  isDisabled =
                    hasSelectedValues &&
                    (fieldValues.some((selectedValueId: string) => specialOptions.includes(selectedValueId) && selectedValueId !== option.value) ||
                      fieldValues.some((selectedValueId: string) => !specialOptions.includes(selectedValueId)));
                } else {
                  isDisabled = fieldValues.some((value) => specialOptions.includes(value));
                }

                return design === 'pill' ? (
                  <Pill key={option.value} className="pill" $selected={isChecked} onClick={() => handleChange(option, !isChecked, arrayHelpers)}>
                    <span>{option.label}</span>
                    <PlusIconStyled $selected={isChecked} />
                  </Pill>
                ) : (
                  <Checkbox
                    key={option.value}
                    name={id}
                    value={option.value}
                    label={option.label}
                    onChange={(event) => handleChange(option, event.target.checked, arrayHelpers)}
                    checked={isChecked}
                    disabled={isDisabled}
                    design={CheckboxDesign.PinkHighlight}
                    showError={false}
                    redFlag={option.redFlag}
                  />
                );
              })}
            </CheckboxesWrap>
          </InnerWrapper>
        )}
      />
      {fieldError && fieldTouched && <FormFieldError message={fieldError} style={{ marginTop: 10 }} />}
    </Container>
  );
};

export default MultiCheckbox;

const Container = styled.div`
  margin-bottom: 40px;
  color: ${Colors.navyBlue};
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const QuestionHeading = styled.div`
  display: flex;
  flex-direction: column;
`;

const CheckboxesWrap = styled.div<{ $design: string }>`
  display: flex;
  gap: 4px;
  margin: 0 -8px;

  ${({ $design }) =>
    $design === 'pill'
      ? `
    flex-wrap: wrap;
  `
      : `
    flex-direction: column;
    justify-content: space-between;
`}
`;

const Pill = styled.div<{ $selected: boolean }>`
  display: inline-flex;
  margin: 0 15px 15px 0;
  padding: 10px 20px;
  align-items: center;
  gap: 10px;
  border-radius: 100px;
  background-color: ${(props) => (props.$selected ? Colors.sage50 : Colors.ecru50)};
  color: ${Colors.navyBlue};
  cursor: pointer;
  user-select: none;
  font-family: 'BrownStd';
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;

  &:hover {
    background-color: ${(props) => (props.$selected ? Colors.sage50 : Colors.sage25)};
  }
`;

const PlusIconStyled = styled(PlusIcon)<{ $selected: boolean }>`
  transform: ${(props) => (props.$selected ? 'rotate(45deg)' : 'rotate(0)')};
  width: 12px;
  height: 12px;
  transition: transform 0.3s;
`;
